import { toast } from 'react-toastify';
import imageCompression from 'browser-image-compression';
import { useState } from 'react';
import { supabase } from 'lib/supabase';
import { useAuthContext } from 'context/authContext';

// Max file size for compression: 2MB
const MAX_FILE_SIZE_MB = 2;
// Mirrors the 'uploads' bucket settings (allowed_mime_types, file_size_limit)
const PHOTO_TYPES = [
  'image/jpeg',
  'image/jpg',
  'image/png',
  'image/gif',
  'image/webp'
];
const BUCKET_LIMIT_MB = 10;

// Shrink a photo before upload. Runs in the page, not a web worker: the
// library's worker downloads itself from a CDN at runtime, and when that is
// unreachable every photo upload fails before anything reaches Supabase.
// If compression itself fails the original file is used instead.
const compressPhoto = async file => {
  try {
    return await imageCompression(file, {
      maxSizeMB: MAX_FILE_SIZE_MB,
      maxWidthOrHeight: 1920, // HD resolution max
      useWebWorker: false
    });
  } catch (error) {
    console.warn('Image compression failed, uploading the original:', error);
    return file;
  }
};

export default function useUpload() {
  const [loading, setLoading] = useState(false);
  const { organization, selectedBranchId } = useAuthContext();

  const upload = async (file, folder = 'general') => {
    setLoading(true);
    try {
      if (!PHOTO_TYPES.includes(file.type)) {
        throw new Error(
          'That file type is not supported. Please use a JPG, PNG, GIF or WEBP photo.'
        );
      }

      const fileToUpload = await compressPhoto(file);
      if (fileToUpload.size > BUCKET_LIMIT_MB * 1024 * 1024) {
        throw new Error(
          `The photo is too large (over ${BUCKET_LIMIT_MB}MB). Please use a smaller one.`
        );
      }

      // Generate unique filename
      const fileExt = file.name.split('.').pop();
      const fileName = `${Date.now()}-${Math.random()
        .toString(36)
        .substring(7)}.${fileExt}`;
      // Include branch_id in path for proper isolation
      const filePath = `${organization.id}/${
        selectedBranchId || 'shared'
      }/${folder}/${fileName}`;

      // Upload to Supabase Storage
      const { data, error } = await supabase.storage
        .from('uploads')
        .upload(filePath, fileToUpload, {
          cacheControl: '3600',
          upsert: false
        });

      if (error) {
        console.error('Supabase upload error:', error);
        throw error;
      }

      // Get public URL
      const {
        data: { publicUrl }
      } = supabase.storage.from('uploads').getPublicUrl(filePath);

      setLoading(false);
      return publicUrl;
    } catch (error) {
      console.error('Upload error:', error);
      toast.error(
        `Photo upload failed: ${error?.message || 'please try again later'}`
      );
      setLoading(false);
      throw error;
    }
  };

  const uploadDocument = async (file, folder = 'general') => {
    setLoading(true);
    try {
      // Generate unique filename
      const fileExt = file.name.split('.').pop();
      const fileName = `${Date.now()}-${Math.random()
        .toString(36)
        .substring(7)}.${fileExt}`;
      // Include branch_id in path for proper isolation
      const filePath = `${organization.id}/${
        selectedBranchId || 'shared'
      }/${folder}/${fileName}`;

      // Upload to documents bucket
      const { data, error } = await supabase.storage
        .from('documents')
        .upload(filePath, file, {
          cacheControl: '3600',
          upsert: false
        });

      if (error) {
        console.error('Supabase upload error:', error);
        throw error;
      }

      // Generate signed URL (expires in 1 year)
      const { data: signedData, error: signedError } = await supabase.storage
        .from('documents')
        .createSignedUrl(filePath, 31536000); // 1 year in seconds

      if (signedError) {
        console.error('Error creating signed URL:', signedError);
        throw signedError;
      }

      setLoading(false);
      return signedData.signedUrl;
    } catch (error) {
      console.error('Upload error:', error);
      toast.error('Something went wrong! Please try again later!');
      setLoading(false);
      throw error;
    }
  };

  const deleteFile = async fileUrl => {
    try {
      // Extract path from URL
      // URL format: https://xxx.supabase.co/storage/v1/object/public/uploads/{path}
      const urlParts = fileUrl.split('/storage/v1/object/public/uploads/');
      if (urlParts.length < 2) {
        throw new Error('Invalid file URL');
      }
      const path = urlParts[1];

      const { error } = await supabase.storage.from('uploads').remove([path]);

      if (error) {
        console.error('Delete error:', error);
        throw error;
      }

      return true;
    } catch (error) {
      console.error('Delete error:', error);
      toast.error('Failed to delete file');
      throw error;
    }
  };

  const deleteDocument = async fileUrl => {
    try {
      if (!fileUrl) return true;

      // Extract path from signed URL or public URL
      // Signed URL format: https://xxx.supabase.co/storage/v1/object/sign/documents/{path}?token=...
      // Public URL format: https://xxx.supabase.co/storage/v1/object/public/documents/{path}
      let filePath = '';

      if (fileUrl.includes('/storage/v1/object/sign/documents/')) {
        filePath = fileUrl
          .split('/storage/v1/object/sign/documents/')[1]
          ?.split('?')[0];
      } else if (fileUrl.includes('/storage/v1/object/public/documents/')) {
        filePath = fileUrl.split('/storage/v1/object/public/documents/')[1];
      }

      if (!filePath) {
        console.warn('Could not extract file path from URL:', fileUrl);
        return false;
      }

      const { error } = await supabase.storage
        .from('documents')
        .remove([filePath]);

      if (error) {
        console.error('Delete document error:', error);
        // Don't throw - just log and continue (old file will remain orphaned)
        return false;
      }

      return true;
    } catch (error) {
      console.error('Delete document error:', error);
      // Don't throw - just log and continue
      return false;
    }
  };

  return {
    upload,
    uploadDocument,
    deleteFile,
    deleteDocument,
    loading
  };
}
